import { z } from 'zod'
import { type RandomBytes, webCryptoRandomBytes } from './random.ts'

/**
 * The tenant build key: a plain, rotatable value that says which tenant a build belongs
 * to — the replacement for the signed, `kid`-rotated build identifier.
 *
 * `docs/DECISIONS.md`, "The build identifier is a plain rotatable key on the tenant row,
 * not a signed, kid-rotated token": the value bought exactly one thing a signature could
 * not — generational revocation — and rotating a plain value on the tenant row expresses
 * that identically, at a fraction of the machinery. It is **public and non-authorizing**,
 * exactly as the signed identifier was: it ships inside every mobile build and is served
 * to any caller by `GET /branding/<slug>.json`, so unguessability is the only property it
 * has to hold, and it holds it the same way every other secret-shaped value in this system
 * does — 256 bits from the platform's CSPRNG, never derived from anything predictable.
 *
 * Deliberately **not** a UUIDv7: a UUIDv7's first 48 bits are a creation timestamp in the
 * clear, which is a property you want in a primary key and never want in a value whose
 * entire job is to be unguessable. Generated here, in `packages/domain`, for the same
 * reason `uuidV7` is (`docs/DECISIONS.md`, "Identifiers are UUIDv7, generated in
 * `packages/domain`"): one CSPRNG call site, reused by `packages/db` (minting the value a
 * new tenant row gets) and by `apps/api` (minting the value a rotation replaces it with).
 */

/** Bytes of randomness a build key carries: 256 bits. */
export const BUILD_KEY_BYTES = 32

/**
 * The shape every build key has, on the way in and on the way out.
 *
 * Base64url, unpadded — the same alphabet `apps/api`'s token encoding already uses, and
 * one that never needs escaping in a header, a URL or a JSON string. The bounds are wide
 * enough to accept a key minted with a different byte count in the future without a schema
 * change: what matters is that the value is opaque and long enough to resist guessing, not
 * its exact length.
 */
export const buildKeySchema = z
  .string()
  .min(32)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/u, {
    error: 'Expected an unpadded base64url string.',
  })
  .meta({
    id: 'BuildKey',
    description:
      'The plain, rotatable value that says which tenant a build belongs to. Public and ' +
      'non-authorizing — it ships inside every download and grants nothing on its own.',
    example: 'kQ2f8b3xN1pW7yV0sT4cA6dE9gH2jL5mO8rU1wZ4bF7-hK',
  })

export type BuildKeyOptions = {
  /** Overridable so a test can pin the bytes and assert the exact encoding. */
  readonly randomBytes?: RandomBytes
}

/**
 * A fresh, unguessable build key.
 *
 * Called once when a tenant is created and once per rotation — never anywhere else, so
 * "how many build keys a tenant has had" is exactly "how many times this function ran for
 * it". Two calls never collide in practice (256 bits of entropy), and the unique index on
 * `tenants.build_key` is what makes that a guarantee rather than a hope.
 */
export function generateBuildKey(options: BuildKeyOptions = {}): string {
  const bytes = (options.randomBytes ?? webCryptoRandomBytes)(BUILD_KEY_BYTES)
  return base64UrlEncode(bytes)
}

/**
 * Base64url, unpadded, over raw bytes — written out rather than reached for `btoa`.
 *
 * `packages/domain` declares no DOM/webworker lib (`docs/ARCHITECTURE.md` §2), and `btoa`
 * is a browser/Worker global outside the ambient types this package compiles against —
 * unlike `crypto`, which `./random.ts` reaches through an explicit `globalThis` cast
 * rather than a type-only global. A hand-written table is the same amount of code as that
 * cast would need anyway, and it has no runtime dependency to assert the presence of.
 */
const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

function base64UrlEncode(bytes: Uint8Array): string {
  let out = ''
  for (let index = 0; index < bytes.length; index += 3) {
    const b0 = bytes[index] ?? 0
    const hasSecond = index + 1 < bytes.length
    const hasThird = index + 2 < bytes.length
    const b1 = bytes[index + 1] ?? 0
    const b2 = bytes[index + 2] ?? 0

    out += BASE64URL_ALPHABET.charAt(b0 >> 2)
    out += BASE64URL_ALPHABET.charAt(((b0 & 0b11) << 4) | (b1 >> 4))
    if (hasSecond) {
      out += BASE64URL_ALPHABET.charAt(((b1 & 0b1111) << 2) | (b2 >> 6))
    }
    if (hasThird) {
      out += BASE64URL_ALPHABET.charAt(b2 & 0b111111)
    }
  }
  return out
}
